
import React, { useState } from 'react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { Subcontractor, ScheduleStatus } from '../types.ts';
import { Plus, Trash2, Users, DollarSign, Activity, HardHat, TrendingUp, Calculator } from 'lucide-react';
import { formatCurrency } from '../services/financialService.ts';
import { Modal } from './Modal.tsx';

export const SubcontractorsPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const subcontractors = useStore(() => api.subcontractors.list(s => s.projectId === projectId), [projectId], 'subcontractors') || [];
  const [adding, setAdding] = useState(false);
  const [newSub, setNewSub] = useState<Partial<Subcontractor>>({
    name: '', trade: '', contractValue: 0, spent: 0, scheduleStatus: 'On Track'
  });

  const save = async () => {
    if (!newSub.name || !newSub.trade) return;
    await api.subcontractors.add({ 
        projectId, 
        name: newSub.name!,
        trade: newSub.trade!,
        contractValue: newSub.contractValue || 0,
        spent: newSub.spent || 0,
        scheduleStatus: (newSub.scheduleStatus as ScheduleStatus) || 'On Track'
    });
    setAdding(false);
    setNewSub({ name: '', trade: '', contractValue: 0, spent: 0, scheduleStatus: 'On Track' });
  };

  const totalContract = subcontractors.reduce((a, b) => a + b.contractValue, 0);
  const totalSpent = subcontractors.reduce((a, b) => a + b.spent, 0);
  const totalRemaining = totalContract - totalSpent;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="grid md:grid-cols-4 gap-6">
         <div className="bg-black p-8 rounded-[2rem] text-white shadow-xl shadow-black/10">
            <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
               <DollarSign size={10}/> Total Commitment
            </div>
            <div className="text-3xl font-black tabular-nums">{formatCurrency(totalContract)}</div>
         </div>
         <div className="bg-white p-8 rounded-[2rem] border border-zinc-200 shadow-sm">
            <div className="text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
               <TrendingUp size={10}/> Actual Costs (Spent)
            </div>
            <div className="text-3xl font-black text-black tabular-nums">{formatCurrency(totalSpent)}</div>
         </div>
         <div className="bg-white p-8 rounded-[2rem] border border-zinc-200 shadow-sm">
            <div className="text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
               <Calculator size={10}/> Liquid Liability
            </div>
            <div className="text-3xl font-black text-rose-500 tabular-nums">{formatCurrency(totalRemaining)}</div>
         </div>
         <button onClick={() => setAdding(true)} className="bg-zinc-50 p-8 rounded-[2rem] border border-dashed border-zinc-200 flex flex-col items-center justify-center hover:bg-white hover:border-black transition-all group">
            <Plus size={24} className="text-zinc-300 mb-2 group-hover:text-black transition-colors"/>
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400 group-hover:text-black transition-colors">Register Partner</span>
         </button>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-zinc-200 overflow-hidden shadow-sm">
         <table className="w-full text-xs text-left">
            <thead className="bg-zinc-50/50 text-[10px] text-zinc-400 font-black uppercase tracking-widest border-b border-zinc-100">
               <tr>
                  <th className="px-10 py-5">Firm / Partner</th>
                  <th className="px-10 py-5">Operational Trade</th>
                  <th className="px-10 py-5">Burn Ratio</th>
                  <th className="px-10 py-5">Health</th>
                  <th className="px-10 py-5 text-right">Commitment</th>
                  <th className="px-10 py-5 text-right">Actual Cost</th>
                  <th className="px-10 py-5 text-right">Remaining</th>
                  <th className="px-4 py-5"></th>
               </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
               {subcontractors.map(sub => (
                  <tr key={sub.id} className="hover:bg-zinc-50/50 transition-colors group">
                     <td className="px-10 py-6">
                        <div className="flex items-center gap-3">
                           <div className="p-2 bg-zinc-100 rounded-lg text-black group-hover:scale-110 transition-transform"><HardHat size={16}/></div>
                           <span className="font-black text-black">{sub.name}</span>
                        </div>
                     </td>
                     <td className="px-10 py-6 text-zinc-500 font-bold uppercase tracking-widest">{sub.trade}</td>
                     <td className="px-10 py-6">
                        <div className="flex flex-col gap-1.5">
                           <div className="w-24 h-1 bg-zinc-100 rounded-full overflow-hidden">
                              <div className="h-full bg-black rounded-full" style={{width: `${Math.min((sub.spent/sub.contractValue)*100, 100)}%`}}></div>
                           </div>
                           <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest">{(sub.spent/sub.contractValue*100).toFixed(0)}% Invoiced</span>
                        </div>
                     </td>
                     <td className="px-10 py-6">
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${
                           sub.scheduleStatus === 'Ahead' ? 'bg-emerald-100 text-emerald-600' :
                           sub.scheduleStatus === 'Behind' ? 'bg-amber-100 text-amber-600' :
                           sub.scheduleStatus === 'Critical' ? 'bg-rose-100 text-rose-600' : 'bg-zinc-100 text-zinc-500'
                        }`}>{sub.scheduleStatus}</span>
                     </td>
                     <td className="px-10 py-6 text-right">
                        <div className="font-black text-black tabular-nums">{formatCurrency(sub.contractValue)}</div>
                        <div className="text-[8px] text-zinc-400 font-black uppercase mt-1">Fixed Fee</div>
                     </td>
                     <td className="px-10 py-6 text-right">
                        <div className="font-black text-black tabular-nums">{formatCurrency(sub.spent)}</div>
                        <div className="text-[8px] text-zinc-400 font-black uppercase mt-1">Disbursed</div>
                     </td>
                     <td className="px-10 py-6 text-right">
                        <div className="font-black text-rose-500 tabular-nums">{formatCurrency(sub.contractValue - sub.spent)}</div>
                        <div className="text-[8px] text-zinc-400 font-black uppercase mt-1">Liability</div>
                     </td>
                     <td className="px-4 py-6 text-right">
                        <button onClick={() => api.subcontractors.delete(sub.id!)} className="p-2 text-zinc-100 hover:text-rose-500 transition-colors">
                           <Trash2 size={16}/>
                        </button>
                     </td>
                  </tr>
               ))}
               {subcontractors.length === 0 && (
                  <tr>
                     <td colSpan={8} className="py-20 text-center text-zinc-300 font-black uppercase tracking-widest text-[10px]">No partners registered in the ledger.</td>
                  </tr>
               )}
            </tbody>
         </table>
      </div>

      <Modal isOpen={adding} onClose={() => setAdding(false)} title="Register Partner Firm" maxWidth="sm">
           <div className="space-y-6">
               <div className="space-y-2">
                  <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Firm Name</label>
                  <input className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-4 font-black focus:bg-white focus:ring-1 focus:ring-black outline-none transition-all" value={newSub.name} onChange={e => setNewSub({...newSub, name: e.target.value})} placeholder="e.g. Pacific Concrete" />
               </div>
               <div className="space-y-2">
                  <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Trade Specialty</label>
                  <input className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-4 font-black focus:bg-white focus:ring-1 focus:ring-black outline-none transition-all" value={newSub.trade} onChange={e => setNewSub({...newSub, trade: e.target.value})} placeholder="e.g. Division 03 - Concrete" />
               </div>
               <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                     <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Contract Commitment ($)</label>
                     <input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-4 font-black focus:bg-white focus:ring-1 focus:ring-black outline-none transition-all" value={newSub.contractValue || ''} onChange={e => setNewSub({...newSub, contractValue: +e.target.value})} />
                  </div>
                  <div className="space-y-2">
                     <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Actual Costs to Date ($)</label>
                     <input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-4 font-black focus:bg-white focus:ring-1 focus:ring-black outline-none transition-all" value={newSub.spent || ''} onChange={e => setNewSub({...newSub, spent: +e.target.value})} />
                  </div>
               </div>
               <div className="space-y-2">
                  <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest block">Schedule Status</label>
                  <select className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-4 font-black focus:bg-white outline-none appearance-none" value={newSub.scheduleStatus} onChange={e => setNewSub({...newSub, scheduleStatus: e.target.value as any})}>
                     <option value="Ahead">Ahead of Plan</option>
                     <option value="On Track">On Track</option>
                     <option value="Behind">Behind Schedule</option>
                     <option value="Critical">Critical Path Delay</option>
                  </select>
               </div>
               <button onClick={save} className="w-full bg-black text-white py-5 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:scale-[1.02] active:scale-95 shadow-xl transition-all">Submit to Ledger</button>
           </div>
      </Modal>
    </div>
  );
};
